// Photo derivative naming, shared by the card (browser) and the image pipeline (node).
// Every width has AVIF and WebP; JPEG exists only at the fallback width (for the rare browser
// without either), which keeps the repository and deploys small.

export const TARGET_WIDTHS = [480, 800, 1200, 2000];
export const MAX_HEIGHT = 2400; // portrait photos stop at 1200 wide (about 2130 tall)

/** Widths to generate for a source of the given size: never upscaled, never taller than MAX_HEIGHT. */
export function widthsFor(sourceWidth, sourceHeight = sourceWidth) {
  const ws = TARGET_WIDTHS.filter((w) => w < sourceWidth && (w * sourceHeight) / sourceWidth <= MAX_HEIGHT);
  if (!ws.length) return [Math.min(sourceWidth, Math.floor((MAX_HEIGHT * sourceWidth) / sourceHeight))];
  return ws;
}

/**
 * object-position y (percent) that centres the focus line (percent from the photo's top) in a
 * frame of boxW × boxH showing an image of height/width `ratio` with object-fit: cover.
 * Plain `object-position: focus%` would only align the focus line with the same line of the
 * frame, which leaves a low building cut off at the bottom of a short frame.
 */
export function coverPositionY(focus, ratio, boxW, boxH) {
  const shown = boxW * ratio; // rendered image height when the width fills the frame
  if (!(shown > boxH)) return 50; // the image is wider than the frame: nothing to shift vertically
  const offset = Math.min(Math.max((focus / 100) * shown - boxH / 2, 0), shown - boxH);
  return Math.round((offset / (shown - boxH)) * 1000) / 10;
}

/** The one width that also has a JPEG. */
export const fallbackWidth = (widths) => widths.filter((w) => w <= 800).at(-1) ?? widths[0];

/** Every file a photo entry needs, relative to public/photos/<slug>/. */
export function derivativeFiles(file, widths) {
  return [
    ...widths.flatMap((w) => [`${file}-${w}.avif`, `${file}-${w}.webp`]),
    `${file}-${fallbackWidth(widths)}.jpg`,
  ];
}
